/**
 * Coin News System - leak -> publish pipeline.
 * 1) At nextLeakAt the next news is decided (pendingNews); 70% of the time a
 *    leak (genuine 60% / fake 40%) is dropped into the feed ahead of it.
 * 2) At nextPublishAt the news goes live (momentum effect on the target coin),
 *    the leak resolves (verdict visible), and the next cycle is scheduled.
 */
import type { TickSystem } from '@app-games/core';
import type { GameState } from '../state.js';
import type { GameAction } from '../actions.js';
import { COINS } from '../config/coins.js';
import { NEWS_CONFIG, templatesForCoin, type NewsDirection } from '../config/coinNews.js';
import { listedCoinIds } from '../exchangeState.js';
import { getLeakLeadBonus } from '../config/prestige.js';
import type { FeedItem, FeedNewsItem, InfoState, LeakItem, PendingNews } from '../infoState.js';
import { drawSeededComments } from '../config/comments.js';

function randRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function makeId(now: number): string {
  return `${now}_${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Decide the next news: random listed coin x random eligible template.
 * Only listed coins are targeted (delisted / pool coins have no live market).
 */
export function drawPendingNews(now: number, eligibleIds: string[]): PendingNews {
  const pool = eligibleIds.length > 0 ? eligibleIds : COINS.map(c => c.id);
  const coinId = pool[Math.floor(Math.random() * pool.length)];
  const templates = templatesForCoin(coinId);
  const tpl = templates[Math.floor(Math.random() * templates.length)];
  return {
    instanceId: makeId(now),
    newsId: tpl.id,
    coinId,
    direction: tpl.direction,
    impact: tpl.impact,
    duration: tpl.duration,
    leakId: makeId(now + 1),
  };
}

/**
 * Build a leak for a pending news.
 * Genuine: claims the real coin + direction. Fake: wrong coin or flipped direction.
 */
export function makeLeak(pending: PendingNews, now: number, revealAt: number, eligibleIds: string[] = []): LeakItem {
  const genuine = Math.random() < NEWS_CONFIG.genuineLeakChance;
  let claimCoinId = pending.coinId;
  let claimDirection: NewsDirection = pending.direction;
  if (!genuine) {
    if (Math.random() < 0.5) {
      const pool = (eligibleIds.length > 0 ? eligibleIds : COINS.map(c => c.id))
        .filter(id => id !== pending.coinId);
      if (pool.length > 0) claimCoinId = pool[Math.floor(Math.random() * pool.length)];
    } else {
      claimDirection = pending.direction === 1 ? -1 : 1;
    }
  }
  return {
    kind: 'leak',
    leakId: pending.leakId,
    variant: 1 + Math.floor(Math.random() * NEWS_CONFIG.leakVariants),
    claimCoinId,
    claimDirection,
    genuine,
    createdAt: now,
    revealAt,
    factChecked: false,
    resolved: false,
  };
}

function publishNews(info: InfoState, now: number, leakLeadBonus: number): InfoState {
  const pending = info.pendingNews;
  if (!pending) return info;
  // Draw this item's NPC comments from the per-direction shuffle bag so the
  // memes cycle without repeating before the pool is exhausted (stored on item).
  const drawn = drawSeededComments(info.bags, pending.direction, now);
  const published: FeedNewsItem = {
    kind: 'news',
    instanceId: pending.instanceId,
    newsId: pending.newsId,
    coinId: pending.coinId,
    direction: pending.direction,
    publishedAt: now,
    seeded: drawn.comments,
  };
  const feed: FeedItem[] = [
    published,
    ...info.feed.map(item =>
      item.kind === 'leak' && item.leakId === pending.leakId
        ? { ...item, resolved: true }
        : item
    ),
  ].slice(0, NEWS_CONFIG.feedLimit);

  const nextPublishAt = now + randRange(NEWS_CONFIG.minInterval, NEWS_CONFIG.maxInterval);
  // buff_ex_leak: 찌라시를 조금 더 일찍 수신 (리드 타임 증가)
  const nextLeakAt = Math.max(
    now + 2000,
    nextPublishAt - randRange(NEWS_CONFIG.leakLeadMin, NEWS_CONFIG.leakLeadMax) - leakLeadBonus
  );

  return {
    ...info,
    bags: drawn.bags,
    feed,
    activeNews: [
      ...info.activeNews,
      {
        instanceId: pending.instanceId,
        newsId: pending.newsId,
        coinId: pending.coinId,
        direction: pending.direction,
        impact: pending.impact,
        publishedAt: now,
        expiresAt: now + pending.duration,
      },
    ],
    pendingNews: null,
    nextLeakAt,
    nextPublishAt,
  };
}

export const coinNewsSystem: TickSystem<GameState, GameAction> = {
  name: 'coinNews',

  update(state) {
    if (!state.isRunning || !state.info || !state.exchange) return state;
    const now = Date.now();
    let info = state.info;
    let changed = false;

    // 1. Lock in the next news; 70% of the time a leak precedes it
    if (!info.pendingNews && now >= info.nextLeakAt) {
      const eligible = listedCoinIds(state.exchange);
      const pending = drawPendingNews(now, eligible);
      const leaked = Math.random() < NEWS_CONFIG.leakChance;
      info = {
        ...info,
        pendingNews: pending,
        feed: leaked
          ? [makeLeak(pending, now, info.nextPublishAt, eligible), ...info.feed].slice(0, NEWS_CONFIG.feedLimit)
          : info.feed,
      };
      changed = true;
    }

    // 2. Publish (respect the concurrent-news cap by delaying)
    if (info.pendingNews && now >= info.nextPublishAt) {
      const activeCount = info.activeNews.filter(n => now < n.expiresAt).length;
      if (activeCount >= NEWS_CONFIG.maxActiveNews) {
        info = { ...info, nextPublishAt: now + 5000 };
      } else {
        info = publishNews(info, now, getLeakLeadBonus(state.prestige.buffs));
      }
      changed = true;
    }

    // 3. Expire finished news effects
    if (info.activeNews.some(n => now >= n.expiresAt)) {
      info = { ...info, activeNews: info.activeNews.filter(n => now < n.expiresAt) };
      changed = true;
    }

    return changed ? { ...state, info } : state;
  },
};
